import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import { z } from 'zod';
import extractValue, {
  type EventOrValue,
} from '../../../store/utils/extractValue';
import Validator, {
  type ValidatorErrors,
} from '../../../store/utils/Validator';
import type Labels from '../../account/store/Labels';
import type Products from '../../products/store/Products';

export default class PulseKeyStatRequest {
  static Schema = z.object({
    label: z.string(),
    productIds: z.array(z.string()).min(1).max(8),
    redeemedAtFrom: z.coerce.date(),
    redeemedAtTo: z.coerce.date(),
  });

  label = '';
  productIds: string[] = [''];
  promise?: IPromiseBasedObservable<PulseKeyStatDto[]> = undefined;
  redeemedAtFrom?: Date;
  redeemedAtTo?: Date;

  readonly labels: Labels;
  readonly products: Products;

  private readonly getStats: GetStatsFunction;
  private readonly validator = new Validator(this, PulseKeyStatRequest.Schema);

  constructor(attrs: {
    getStats: GetStatsFunction;
    initialStats?: PulseKeyStatDto[];
    labels: Labels;
    products: Products;
    redeemedAtFrom?: Date;
    redeemedAtTo?: Date;
  }) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.getStats = attrs.getStats;
    this.labels = attrs.labels;
    this.products = attrs.products;
    this.promise = fromPromise.resolve(
      attrs.initialStats || [],
    ) as IPromiseBasedObservable<PulseKeyStatDto[]>;
    this.redeemedAtFrom = attrs.redeemedAtFrom;
    this.redeemedAtTo = attrs.redeemedAtTo;
  }

  get errors(): ValidatorErrors<PulseKeyStatParams> {
    return this.validator.errorsForEnabledFields;
  }

  handleBlur(e: EventOrValue<keyof PulseKeyStatRequest>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    Object.assign(this, { [name]: value });

    if (this.validator.validate()) {
      this.update();
    }
  }

  update() {
    this.promise = this.getStats({
      label: this.label,
      productIds: this.productIds,
      redeemedAtFrom: this.redeemedAtFrom,
      redeemedAtTo: this.redeemedAtTo,
    });
  }
}

export interface PulseKeyStatDto {
  productId: string;
  stats: PulseKeyStatRecord[];
}

export interface PulseKeyStatRecord {
  keyCount: number;
  redeemedDate: string;
}

export interface PulseKeyStatParams {
  label?: string;
  productIds?: string[];
  redeemedAtFrom?: Date;
  redeemedAtTo?: Date;
}

export type GetStatsFunction = (
  params: PulseKeyStatParams,
) => IPromiseBasedObservable<PulseKeyStatDto[]>;
